/**
 * Shared display vocabulary for the policy module.
 *
 * BM-H331 established the closed enums: the backend stores `owner_role` and
 * `tier` as closed enum strings (hub/policy_models.py), and the real Tier
 * values are core / tier-2 / yellow / sop / white. The badge used to test for
 * "red", which is not a Tier value at all.
 *
 * TIER BADGE COLOURS ARE INLINE, DELIBERATELY.
 *
 * The Tier column rendered an empty white pill for every `white` tier policy
 * in production. The label text was there. It was invisible. The badge used
 * the Tailwind pair `bg-gray-100 text-gray-800`, and the app-wide dark theme
 * in src/index.css contains
 *
 *     [class*="text-gray-800"] { color: #F4F1EA; }
 *
 * with no matching override for `bg-gray-100`. So the pill kept Tailwind's
 * light #F3F4F6 background while the text was repainted near-white: contrast
 * ratio 1.01, i.e. a blank pill. Every other tier survived only because
 * index.css also overrides its background.
 *
 * Colours therefore come from `tierBadgeStyle`, an inline style object, which
 * no global stylesheet rule can repaint (no `!important` involved). Never move
 * tier colours back into Tailwind colour utilities.
 *
 * A value the badge does not recognise, and a missing value, are shown loudly
 * rather than swallowed. A blank badge tells the reader nothing.
 */

export const OWNER_ROLE_LABELS = {
  mlco: "MLCO",
  colp: "COLP",
  mlro: "MLRO",
  cofa: "COFA",
  dpo: "DPO",
  director: "Director",
  complaints_manager: "Complaints Manager",
};

export function ownerRoleLabel(role) {
  if (!role) return "-";
  return OWNER_ROLE_LABELS[role] || role;
}

/** Every value of the backend `Tier` enum, and the words shown for it. */
export const TIER_LABELS = {
  core: "Core",
  "tier-2": "Tier 2",
  yellow: "Yellow",
  sop: "SOP",
  white: "White",
};

/** Shown when a policy row carries no tier at all. */
export const TIER_MISSING_LABEL = "No tier set";

/** Shape only. Colour never travels in a class name — see the header note. */
export const TIER_BADGE_CLASS =
  "inline-block px-2 py-0.5 rounded text-xs font-medium border align-middle";

const TIER_STYLES = {
  core: { backgroundColor: "#FEE2E2", color: "#7F1D1D", borderColor: "#FCA5A5" },
  "tier-2": { backgroundColor: "#FFEDD5", color: "#7C2D12", borderColor: "#FDBA74" },
  yellow: { backgroundColor: "#FEF9C3", color: "#713F12", borderColor: "#CA8A04" },
  sop: { backgroundColor: "#DBEAFE", color: "#1E3A8A", borderColor: "#93C5FD" },
  white: { backgroundColor: "#FFFFFF", color: "#1F2937", borderColor: "#6B7280" },
};

/** Loud amber, for a tier that is missing or that this build does not know. */
const TIER_STYLE_UNRECOGNISED = {
  backgroundColor: "#FEF3C7",
  color: "#7C2D12",
  borderColor: "#B45309",
};

function normalise(tier) {
  if (typeof tier === "string") return tier.trim();
  if (tier === null || tier === undefined) return "";
  return String(tier);
}

/**
 * Words for a tier value. Never returns an empty string, for any input.
 *
 *   known enum value -> its label, e.g. "White"
 *   missing / null   -> "No tier set"
 *   anything else    -> "Unknown tier: <value>", so a new enum value that
 *                       nobody taught this file about is visible on the page
 *                       instead of hiding as a blank pill.
 */
export function tierLabel(tier) {
  const value = normalise(tier);
  if (!value) return TIER_MISSING_LABEL;
  if (Object.prototype.hasOwnProperty.call(TIER_LABELS, value)) {
    return TIER_LABELS[value];
  }
  const shown = value.length > 40 ? `${value.slice(0, 40)}...` : value;
  return `Unknown tier: ${shown}`;
}

/** Inline colours for the badge. Always returns a legible pair. */
export function tierBadgeStyle(tier) {
  const value = normalise(tier);
  return TIER_STYLES[value] || TIER_STYLE_UNRECOGNISED;
}

/** True when this build has a label for the value. Used by tests and callers. */
export function isKnownTier(tier) {
  const value = normalise(tier);
  return Object.prototype.hasOwnProperty.call(TIER_LABELS, value);
}

/**
 * Kept for callers that only want the shape classes. It returns no colour
 * utility, on purpose: colour comes from `tierBadgeStyle`.
 */
export function tierBadgeClass() {
  return TIER_BADGE_CLASS;
}
